/**
 * MonitorPage — the home page ('/') of the monitor-only app.
 *
 * Monitor 2.0: every probe the Apex reports gets a configurable card
 * (show/hide, order, md/lg size, target range with reef presets), a
 * range picker (1h → 30d), a connection badge, and a two-probe compare
 * chart. All preferences persist on the Pi via
 * `useServerBackedState('monitor-config')` so every device shares one
 * layout. Strictly read-only: nothing on this page writes to hardware.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from '../components/Badge';
import { useApexSnapshot } from '../hooks/useApexSnapshot';
import { useServerBackedState } from '../hooks/useServerBackedState';
import { apexConnectionStatus, formatAgeSeconds } from '../lib/apex-status';
import { CompareChart } from '../monitor/CompareChart';
import { ProbeCard } from '../monitor/ProbeCard';
import {
  DEFAULT_RANGE_HOURS,
  RANGE_OPTIONS,
  defaultConfigFromProbes,
  mergeConfigWithProbes,
  moveProbe,
  readLegacyRangeHours,
  sanitizeMonitorConfig,
  setProbeTarget,
  updateProbe,
  type ComparePair,
  type MonitorConfig,
} from '../monitor/monitorConfig';

const HOUR_MS = 60 * 60 * 1_000;

const CONNECTION_BADGE_VARIANT = {
  connected: 'ok',
  stale: 'warn',
  offline: 'danger',
} as const;

const CONNECTION_BADGE_LABEL = {
  connected: 'connected',
  stale: 'stale',
  offline: 'offline',
} as const;

export function MonitorPage() {
  const { snapshot, nowMs, error } = useApexSnapshot();

  // Seed the very first config from the pre-2.0 localStorage range so
  // the operator's old choice survives the upgrade (harmless if absent).
  const [defaultConfig] = useState<MonitorConfig>(() => ({
    probes: [],
    rangeHours: readLegacyRangeHours() ?? DEFAULT_RANGE_HOURS,
    compare: null,
  }));
  const {
    value: stored,
    setValue,
    loading: configLoading,
  } = useServerBackedState<MonitorConfig>('monitor-config', defaultConfig);

  const liveProbes = useMemo(() => snapshot?.apex?.probes ?? [], [snapshot]);

  // Sanitize whatever came off the Pi, then reconcile with the live
  // probe catalog: new probes auto-append, absent ones keep their slot.
  const config = useMemo(
    () =>
      mergeConfigWithProbes(
        sanitizeMonitorConfig(stored, defaultConfig.rangeHours),
        liveProbes,
      ),
    [stored, liveProbes, defaultConfig.rangeHours],
  );

  const commit = useCallback((next: MonitorConfig) => setValue(next), [setValue]);

  const [customize, setCustomize] = useState(false);

  // Freeze the window per range selection so child charts don't
  // refetch every poll tick. Refreshes when the range changes.
  const [toMs, setToMs] = useState(() => Date.now());
  useEffect(() => {
    setToMs(Date.now());
  }, [config.rangeHours]);
  const fromMs = toMs - config.rangeHours * HOUR_MS;

  const liveByName = useMemo(() => {
    const m = new Map<string, { value: number; unit: string | null; probeType: string }>();
    for (const p of liveProbes) {
      if (!m.has(p.name)) {
        m.set(p.name, { value: p.value, unit: p.unit, probeType: p.probeType });
      }
    }
    return m;
  }, [liveProbes]);

  const visibleProbes = config.probes.filter((p) => p.visible);
  const hiddenProbes = config.probes.filter((p) => !p.visible);

  const fetchedAtMs = snapshot?.apex?.fetchedAtMs;
  const connection = apexConnectionStatus(fetchedAtMs, nowMs);
  const ageLabel = formatAgeSeconds(fetchedAtMs, nowMs);

  // Effective compare pair: the persisted pair when both probes are
  // still visible, otherwise fall back to the first two visible probes.
  const visibleIds = visibleProbes.map((p) => p.id);
  let pair: ComparePair | null = null;
  if (visibleIds.length >= 2) {
    const storedPair = config.compare ?? null;
    const a =
      storedPair !== null && visibleIds.includes(storedPair.a)
        ? storedPair.a
        : visibleIds[0]!;
    const b =
      storedPair !== null && visibleIds.includes(storedPair.b)
        ? storedPair.b
        : (visibleIds.find((id) => id !== a) ?? visibleIds[1]!);
    pair = { a, b };
  }

  const resetToDefaults = (): void => {
    if (
      window.confirm(
        'Reset the monitor layout to defaults? This rebuilds every card from the live Apex probes with preset targets.',
      )
    ) {
      commit(defaultConfigFromProbes(liveProbes, config.rangeHours));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <header className="mb-4 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-2xl font-bold text-reef-100">Monitor</h1>
            <Badge
              variant={CONNECTION_BADGE_VARIANT[connection]}
              live={connection === 'connected'}
              data-testid="monitor-connection"
            >
              {CONNECTION_BADGE_LABEL[connection]}
            </Badge>
            {fetchedAtMs !== undefined ? (
              <span
                className="text-xs text-reef-400 tabular-nums"
                data-testid="monitor-age"
              >
                last poll {ageLabel} ago
              </span>
            ) : null}
          </div>
          <p className="text-sm text-reef-400 mt-1">
            Live Apex probes, trends &amp; targets — read-only.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Range picker */}
          <div
            className="inline-flex bg-reef-800 border border-reef-700 rounded-lg p-0.5"
            data-testid="monitor-range"
          >
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.hours}
                type="button"
                onClick={() => commit({ ...config, rangeHours: r.hours })}
                data-testid={`monitor-range-${r.hours}`}
                className={`px-2.5 py-1.5 text-xs font-semibold rounded transition-colors ${
                  config.rangeHours === r.hours
                    ? 'bg-amber-600 text-white'
                    : 'text-reef-300 hover:bg-reef-700'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {/* Customize toggle */}
          <button
            type="button"
            data-testid="monitor-customize"
            onClick={() => setCustomize((c) => !c)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
              customize
                ? 'border-amber-500 bg-amber-600 text-white'
                : 'border-reef-700 bg-reef-800 text-reef-200 hover:bg-reef-700'
            }`}
          >
            {customize ? 'Done' : 'Customize'}
          </button>
        </div>
      </header>

      {/* Customize toolbar */}
      {customize ? (
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap rounded-xl border border-amber-600/40 bg-amber-500/5 px-4 py-3">
          <p className="text-xs text-reef-300">
            Customize mode — hide, reorder or resize cards and edit target
            ranges. Changes save to the Pi for every device.
          </p>
          <button
            type="button"
            data-testid="monitor-reset"
            onClick={resetToDefaults}
            className="rounded-md border border-reef-700 bg-reef-800 px-2.5 py-1 text-[11px] font-semibold text-reef-200 hover:bg-reef-700"
          >
            Reset to defaults
          </button>
        </div>
      ) : null}

      {/* Hidden probes strip */}
      {customize && hiddenProbes.length > 0 ? (
        <div
          data-testid="hidden-probes"
          className="mb-4 flex items-center gap-2 flex-wrap rounded-xl border border-reef-800 bg-reef-900 px-4 py-3"
        >
          <span className="text-xs font-semibold uppercase tracking-wider text-reef-500">
            Hidden
          </span>
          {hiddenProbes.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`probe-show-${p.id}`}
              onClick={() => commit(updateProbe(config, p.id, { visible: true }))}
              className="rounded-full border border-reef-700 bg-reef-800 px-3 py-1 text-xs text-reef-200 hover:bg-reef-700"
            >
              {p.id} — show
            </button>
          ))}
        </div>
      ) : null}

      {config.probes.length === 0 ? (
        <div
          className="text-center py-20 bg-reef-900 border border-reef-800 rounded-xl"
          data-testid="monitor-empty"
        >
          <p className="text-lg font-semibold text-reef-300">
            {configLoading ? 'Loading your monitor…' : 'Waiting for Apex data…'}
          </p>
          <p className="text-sm text-reef-500 mt-2">
            {error !== null
              ? `Agent unreachable: ${error}`
              : 'No probes in the latest snapshot yet.'}
          </p>
        </div>
      ) : (
        <>
          {/* Probe cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleProbes.map((p, i) => {
              const live = liveByName.get(p.id);
              return (
                <ProbeCard
                  key={p.id}
                  id={p.id}
                  probeType={live?.probeType ?? null}
                  unit={live?.unit ?? null}
                  liveValue={live !== undefined ? live.value : null}
                  target={p.target ?? null}
                  size={p.size}
                  fromMs={fromMs}
                  toMs={toMs}
                  customize={customize}
                  canMoveUp={i > 0}
                  canMoveDown={i < visibleProbes.length - 1}
                  onHide={() => commit(updateProbe(config, p.id, { visible: false }))}
                  onMove={(dir) => commit(moveProbe(config, p.id, dir))}
                  onToggleSize={() =>
                    commit(
                      updateProbe(config, p.id, {
                        size: p.size === 'md' ? 'lg' : 'md',
                      }),
                    )
                  }
                  onTargetChange={(t) => commit(setProbeTarget(config, p.id, t))}
                />
              );
            })}
          </div>

          {/* Compare */}
          {pair !== null ? (
            <div className="mt-4">
              <CompareChart
                options={visibleProbes.map((p) => ({
                  id: p.id,
                  unit: liveByName.get(p.id)?.unit ?? null,
                }))}
                pair={pair}
                onChange={(next) => commit({ ...config, compare: next })}
                fromMs={fromMs}
                toMs={toMs}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
